import { Injectable } from '@angular/core';

/** Halo texture url plus the scale of the halo sprite relative to the marker sprite. */
export type MarkerStateIcon = { icon: string; scale: number };
export type MarkerIcons = { default: string; selected?: MarkerStateIcon };

/** Marker size in design px (Figma "3D icon" is a 64px circle). */
const MARKER_DESIGN_SIZE = 64;
/** Texture px per design px; 4x keeps the 2px ring crisp when the marker is close to the camera. */
const PIXEL_RATIO = 4;
/**
 * Figma "3D icon / Active" state, in design px:
 * `stroke: 2px #4670DB` around the circle + `box-shadow: 0 0 0 7px rgba(70,112,219,.3)`.
 */
const RING_WIDTH = 2;
const RING_COLOR = '#4670db';
const HALO_WIDTH = 7;
const HALO_COLOR = 'rgba(70, 112, 219, 0.3)';

/**
 * Builds the "selected" halo shared by every circular scan marker (assets, tickets, events):
 * the ring + translucent band drawn around a marker while its element is open on the side sheet.
 * The texture is transparent in the middle, so the scan library renders it on a separate,
 * alpha-blended sprite over the (opaque) marker icon (`SpriteConfiguration.selectedHalo`).
 */
@Injectable({ providedIn: 'root' })
export class MarkerStateIconService {
  private _selectedHalo: MarkerStateIcon | undefined;
  private _drawn = false;

  /** `undefined` only when a 2D canvas is unavailable (e.g. jsdom); the marker then has no halo. */
  get selectedHalo(): MarkerStateIcon | undefined {
    if (!this._drawn) {
      this._selectedHalo = this._drawSelectedHalo();
      this._drawn = true;
    }
    return this._selectedHalo;
  }

  markerIcons(baseUrl: string): MarkerIcons {
    return { default: baseUrl, selected: this.selectedHalo };
  }

  private _drawSelectedHalo(): MarkerStateIcon | undefined {
    const markerSize = MARKER_DESIGN_SIZE * PIXEL_RATIO;
    const ring = RING_WIDTH * PIXEL_RATIO;
    const halo = HALO_WIDTH * PIXEL_RATIO;
    const size = markerSize + 2 * (ring + halo);
    const center = size / 2;
    const circleRadius = markerSize / 2;

    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) return undefined;

    context.beginPath();
    context.arc(center, center, circleRadius + ring + halo, 0, Math.PI * 2);
    context.arc(center, center, circleRadius + ring, 0, Math.PI * 2, true);
    context.fillStyle = HALO_COLOR;
    context.fill('evenodd');

    context.beginPath();
    context.arc(center, center, circleRadius + ring / 2, 0, Math.PI * 2);
    context.lineWidth = ring;
    context.strokeStyle = RING_COLOR;
    context.stroke();

    return { icon: canvas.toDataURL('image/png'), scale: size / markerSize };
  }
}
